import React, { useEffect, useState } from 'react';
import { DropTargetMonitor, useDrop } from 'react-dnd';
import classNames from 'classnames';
import fillStyle from './FillStyle.module.css';

export const DEFAULT_GROUP = '__TORQDRAGANDDROP_DEFAULT_GROUP';

enum DragState {
  Idle,
  Inside,
  Outside,
  Drop,
}

export interface DroppableProps<DraggableItem> {
  /** The group or array of groups this Droppable accepts. Drag events will only
   * only be triggered by Draggables with a matching group. If no value is set,
   * a default group name is assigned instead
   */
  group?: string | symbol | (string | symbol)[];

  /** Completely disables all events and drop styling */
  disabled?: boolean;

  /**
   * Whether or not to fill the parent
   */
  fill?: boolean;

  /** Style to be applied to the wrapping drop zone at all times */
  style?: React.CSSProperties;

  /** Style to be applied when a valid Draggable has been dragged over. Can be called
   * as a function for conditional styling based on what item is being dragged over
   */
  dropStyle?:
    | React.CSSProperties
    | ((item: DraggableItem) => React.CSSProperties);

  /** CSS class to be applied to the wrapping drop zone at all times */
  className?: string;

  /** CSS class to be applied when a valid Draggable has been dragged over. Can be called
   * as a function for conditional styling based on what item is being dragged over
   */
  dropClassName?: string | ((item: DraggableItem) => string);

  /** Called when an item is dragged over this
   * @param item The item on the Draggable that was dragged into this*/
  onDragIn?(item: DraggableItem): void;
  /** Called when an item is dragged out of this without dropping
   * @param item The item on the Draggable that was dragged out of this */
  onDragOut?(item: DraggableItem): void;
  /** Called when an item is dropped into this
   * @param item The item on the Draggable that was dropped into this
   */
  onDrop?(item: DraggableItem): void;
}

const Droppable = <Item extends object>(
  props: DroppableProps<Item> & { children?: React.ReactNode }
) => {
  const {
    group,
    disabled,
    fill,
    style,
    dropStyle,
    className = '',
    dropClassName = '',
    onDragIn,
    onDragOut,
    onDrop,
    children,
  } = props;

  //To convert the default behavior (29384732 hover events followed by a drop) into a
  // dragIn, dragOut, drop event set, we need to track our dragging state
  const [dragState, setDragState] = useState(DragState.Idle);

  //Instead of directly firing the three events via our hook, we simply use the hook
  // to manage the drag state of our item relative to this dropper
  const [{ isDraggingOver, draggedItem }, drop] = useDrop({
    accept: group ?? DEFAULT_GROUP,
    hover: (draggable: Item, monitor) =>
      dragEvent(draggable, monitor, () => {
        setDragState(DragState.Inside);
      }),
    drop: (draggable: Item, monitor) =>
      dragEvent(draggable, monitor, () => {
        setDragState(DragState.Drop);
        if (onDrop) {
          onDrop(draggable);
        }
      }),
    collect: (monitor) => ({
      isDraggingOver: monitor.isOver({ shallow: true }) && monitor.canDrop(),
      draggedItem: monitor.getItem() as Item | null,
    }),
    canDrop: () => !disabled,
  });

  //useDrop will tell us when the droppable is hovered over, but it won't let us know when
  // a draggable has been dragged OUT of a droppable. We need to track that ourselves
  useEffect(() => {
    if (!isDraggingOver && dragState == DragState.Inside) {
      setDragState(DragState.Outside);
    }
  }, [isDraggingOver, dragState]);

  //Within this effect, we PURELY listen to changes to the dragState in order to fire
  // fire events. The dragged item remains consistent throughout all events so we don't
  // need to worry about firing off null items
  useEffect(() => {
    if (!draggedItem) {
      return;
    }

    if (dragState == DragState.Inside && onDragIn) {
      onDragIn(draggedItem);
    } else if (dragState == DragState.Outside && onDragOut) {
      onDragOut(draggedItem);
    } else if (dragState == DragState.Drop && onDrop) {
      onDrop(draggedItem);

      //The only difference here between setting our state to Idle vs Outside is that Idle
      // doesn't trigger the onDragOut event
      setDragState(DragState.Idle);
    }
  }, [dragState]);

  function dragEvent(
    draggable: Item,
    monitor: DropTargetMonitor,
    callback?: (draggable: Item) => void
  ) {
    if (callback && monitor.isOver({ shallow: true }) && monitor.canDrop()) {
      callback(draggable);
    }
  }

  function getDropStyle(): React.CSSProperties {
    if (dropStyle) {
      if (typeof dropStyle == 'function' && draggedItem != null) {
        return dropStyle(draggedItem);
      } else if (typeof dropStyle == 'object') {
        return dropStyle;
      }
    }

    return {};
  }

  function getDropClassName(): string {
    if (typeof dropClassName == 'function' && draggedItem != null) {
      return dropClassName(draggedItem);
    } else if (typeof dropClassName == 'string') {
      return dropClassName;
    }

    return '';
  }

  return (
    <div
      ref={drop}
      style={
        dragState == DragState.Inside ? { ...style, ...getDropStyle() } : style
      }
      className={classNames(className, {
        [getDropClassName()]: dragState == DragState.Inside,
        [fillStyle.fill]: fill,
      })}
    >
      {children}
    </div>
  );
};

Droppable.displayName = 'Droppable';
export { Droppable };
